// Library Methods
import { useContext, useMemo } from "react";
import { Trans, useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import HeadsetFitCard from "../SynoptophoreGraph/shared/HeadsetFitCard";
import SummaryCard from "../SynoptophoreGraph/shared/SummaryCard";
import DirectionColumn from "./DirectionColumn";
import SizeScale from "./SizeScale";

// Contexts
import { ReportSectionContext } from "src/contexts/reports/ReportSectionContext";

// Types
import {
  AniseikoniaMeridianKeyType,
  AniseikoniaMeridianSummaryType,
} from "src/services/exams/types";

// Utils
import {
  HEADLINE_NAMES,
  MERIDIANS,
  SIGNIFICANT_PERCENT,
  duration,
  largerEyeWord,
  largestDirection,
} from "./aniseikoniaHelpers";

const AniseikoniaGraph = () => {
  const { t } = useTranslation();
  const { examReport } = useContext(ReportSectionContext);

  const summary = examReport?.postProcessing?.summary;
  const directComparison = summary?.DirectComparison;
  const module = examReport?.postProcessing?.modules?.DirectComparison;
  const instrument = examReport?.postProcessing?.instrument;
  const calibration = examReport?.postProcessing?.calibration;
  const geometry = examReport?.exam?.targetGeometry;

  // Every direction the section was ordered to run, in a fixed order so two reports from the
  // same clinic read the same way.
  const measured = useMemo(
    () =>
      MERIDIANS.map((meridian) => ({
        ...meridian,
        summary: directComparison?.[meridian.key] as
          | AniseikoniaMeridianSummaryType
          | null
          | undefined,
      })).filter(
        (
          meridian
        ): meridian is (typeof MERIDIANS)[number] & {
          summary: AniseikoniaMeridianSummaryType;
        } =>
          Boolean(meridian.summary) &&
          meridian.summary?.percent !== null &&
          meridian.summary?.percent !== undefined
      ),
    [directComparison]
  );

  const headline = useMemo(() => largestDirection(measured), [measured]);

  const points = useMemo(
    () =>
      measured.map((meridian) => ({
        name: t(meridian.label),
        percent: meridian.summary.percent,
      })),
    [measured, t]
  );

  if (!directComparison) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Typography fontSize="14px" fontWeight={600} color="#E10C0C">
          {t("No Data Available.")}
        </Typography>
      </Box>
    );
  }

  const headlineEye = largerEyeWord(headline?.summary.percent);

  return (
    <Box display="flex" flexDirection="column" width="100%" gap="16px">
      {/* The headset leads. Everything below it is a statement about the patient that is only
          worth reading if this card is good. */}
      <HeadsetFitCard
        residualDegrees={calibration?.residualDegrees}
        qualityFloorMet={calibration?.qualityFloorMet}
        interpupillaryDistanceMetres={instrument?.interpupillaryDistanceMetres}
        interpupillaryDistanceMeasured={
          instrument?.interpupillaryDistanceMeasured
        }
        withinCharacterisedFitRange={instrument?.withinCharacterisedFitRange}
      />

      {/* The result as a sentence, then the one picture that carries all three directions. */}
      <SummaryCard title={t("Result")} cautions={null}>
        <Box
          sx={{
            pb: "8px",
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          <Typography
            fontSize="18px"
            fontWeight={700}
            sx={{ lineHeight: 1.35 }}
          >
            {headline === null || headlineEye === null ? (
              t(
                "The two eyes see the image at the same size in every direction."
              )
            ) : (
              <Trans
                t={t}
                i18nKey="{{direction}}, the <eye>{{eye}}</eye> eye's image is {{percent}}% larger than the {{other}}."
                values={{
                  direction: t(HEADLINE_NAMES[headline.key]),
                  eye: t(headlineEye),
                  percent: Math.abs(headline.summary.percent).toFixed(1),
                  other: t(headlineEye === "left" ? "right" : "left"),
                }}
                components={{ eye: <em /> }}
              />
            )}
          </Typography>

          {points.length > 0 && (
            <SizeScale points={points} significantAt={SIGNIFICANT_PERCENT} />
          )}

          <Typography fontSize="12px" color="#5B6D7A">
            {t("Each dot is one direction the patient matched.")}
          </Typography>

          <Typography fontSize="12px" color="#5B6D7A">
            {t("Duration")}: {duration(module?.durationSeconds)}
          </Typography>
        </Box>
      </SummaryCard>

      {/* The three directions share one card, side by side. */}
      <SummaryCard title={t("Each Direction")} cautions={null}>
        <Box
          sx={{
            pb: "8px",
            display: "grid",
            gap: "8px",
            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
          }}
        >
          {measured.map((meridian) => (
            <DirectionColumn
              key={meridian.key}
              meridian={meridian.key as AniseikoniaMeridianKeyType}
              title={t(meridian.label)}
              plainName={t(meridian.plainName)}
              summary={meridian.summary}
              result={module?.meridians?.[meridian.key]}
              geometry={geometry}
              significantAt={SIGNIFICANT_PERCENT}
            />
          ))}
        </Box>
      </SummaryCard>
    </Box>
  );
};

export default AniseikoniaGraph;
